"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import SubscribeForm from "@/components/templates/SubscribeForm";
import {
  buildFormInitialData,
  buildFormSchema,
  normalizeSubscribeData,
} from "@/components/templates/SubscribeForm/form-data";
import { SubscribeFormData } from "@/components/templates/SubscribeForm/types";
import { SUBSCRIBE_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useGetCustomerById from "@/hooks/requests/customer/use-get-customer-by-id";
import useGetAllCustomerSalesProducts from "@/hooks/requests/customer-sales-product/use-get-all-customer-sales-product";
import useGetSalesProductById from "@/hooks/requests/sales-product/use-get-sales-product-by-id";
import useGetSalesProductPriceById from "@/hooks/requests/sales-product-price/use-get-sales-product-price-by-id";
import useCreateSubscription from "@/hooks/requests/subscription/use-create-subscription";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { SubscribeMessages } from "@/messages/subscribe/subscribe.messages";
import customerSalesProductPaymentService from "@/services/customer-sales-product-payment-service";
import { findContractedPlan } from "@/helpers/customer-sales-product-helpers";
import { CustomerSalesProductPaymentStatus } from "@/types/customer-sales-product-payment-status.types";
import { FilterType } from "@/types/filter.types";

type Props = {
  messages: SubscribeMessages;
};

function SubscribePage({ messages }: Props) {
  const { lang } = useLanguage();
  const { salesproductpriceId } = useParams();
  const { checkPermissions } = useAuth();
  const customerId = useGetCustomerId();
  const _id = Number(customerId);
  const router = useNavigate();
  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerById({ _id });
  const { create, isLoading: isLoadingSubmit } = useCreateSubscription();
  const { data: existingPlans, isLoading: isLoadingPlans } = useGetAllCustomerSalesProducts({
    pageNumber: 1,
    pageSize: 50,
  });
  const hasBlockingSubscription = !!findContractedPlan(existingPlans?.items ?? []);
  const hasNoPermissionToSubscribe = checkPermissions(SUBSCRIBE_PERMISSIONS.CREATE);

  useEffect(() => {
    if (hasNoPermissionToSubscribe) router(ROUTE_URLS.serviceDashboard(lang));
  }, [hasNoPermissionToSubscribe, lang, router]);

  const {
    data: salesProductPrice,
    isLoading: isLoadingSalesProductPrice,
    error: errorSalesProductPrice,
  } = useGetSalesProductPriceById({
    _id: Number(salesproductpriceId) || 0,
  });

  const {
    data: salesProduct,
    isLoading: isLoadingSalesProduct,
    error: errorSalesProduct,
  } = useGetSalesProductById({
    _id: salesProductPrice ? salesProductPrice.salesProductId : 0,
  });

  const BREAD_CRUMBS = [
    {
      title: messages.breadCrumbTitle,
      href: ROUTE_URLS.subscribe(lang, Number(salesproductpriceId)),
    },
  ];

  const isLoading = isLoadingSalesProductPrice || isLoadingSalesProduct || isLoadingCustomer || isLoadingPlans;
  const error = errorSalesProduct || errorSalesProductPrice;
  const shouldLoadError = !isLoading && !!error;
  const shouldLoadBlocked = !isLoading && hasBlockingSubscription;
  const shouldLoadForm = !!salesProduct && !error && !!salesProductPrice && !!customer && !hasBlockingSubscription;

  const resolveInvoiceUrl = async (response: {
    invoiceUrl?: string;
    customerSalesProductId?: number;
  }): Promise<string | null> => {
    if (response?.invoiceUrl) return response.invoiceUrl;

    const customerSalesProductId = response?.customerSalesProductId;
    if (!customerSalesProductId) return null;

    try {
      const payments = await customerSalesProductPaymentService.filter(
        [
          {
            property: "customerSalesProductId",
            value: customerSalesProductId,
            filterType: FilterType.Equals,
          },
        ],
        { pageNumber: 1, pageSize: 5 },
        "billingDate",
        "desc"
      );

      const unpaidWithInvoice = payments?.items?.find(
        (payment) =>
          !!payment.invoiceUrl &&
          (payment.status === CustomerSalesProductPaymentStatus.BillingSent ||
            payment.status === CustomerSalesProductPaymentStatus.New)
      );

      return unpaidWithInvoice?.invoiceUrl || null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (_formData: SubscribeFormData) => {
    if (!customer) return;

    const successMessage = messages.planCreateSuccess;
    const errorMessage = messages.planCreateError;

    const formData = normalizeSubscribeData(_formData, customer);

    const response = await create({ formData, successMessage, errorMessage });
    if (!response) return;

    const invoiceUrl = await resolveInvoiceUrl(response);
    if (invoiceUrl) {
      window.location.href = invoiceUrl;
      return;
    }

    router(ROUTE_URLS.customerSubscriptionTab(lang));
  };

  return (
    <PageLayout messages={messages} title={messages.title} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoading}>
          {shouldLoadForm && (
            <Formik
              initialValues={buildFormInitialData(customer, salesProduct, salesProductPrice)}
              validationSchema={buildFormSchema(messages)}
              validateOnChange={true}
              onSubmit={handleSubmit}
            >
              <SubscribeForm messages={messages} isLoading={isLoadingSubmit} />
            </Formik>
          )}

          {!!shouldLoadError && (
            <AlertBlock
              title={messages.salesProductErrorToLoad}
              description={error.message}
              alt={messages.salesProductNotFoundAlt}
            />
          )}

          {shouldLoadBlocked && (
            <AlertBlock
              title={messages.planCreateError}
              description="Sua empresa já possui uma assinatura ativa ou aguardando pagamento."
              alt={messages.planCreateError}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default SubscribePage;
